import React from 'react';
import keyBy from 'lodash/keyBy';

import { TFunction } from '@lyearn/i18n';
import { Menu2, Menu3 } from '@lyearn/icons';
import { Field, FieldType } from '@/components/Form';
import { renderMediumOptionLabel } from '@/components/Form/components/Select/optionLabelRenderer';
import { renderMediumSelectValue } from '@/components/Form/components/Select/renderValue';
import { FORM_PICKLIST_PROPS } from '@/modules/corporate/components/picklist';
import { DatabaseFieldEditorConfig } from '@/modules/corporate/modules/database/fields/types';
import { DatabaseTextFormatEnum } from '@/types/schema';

import * as FieldUtils from '../../utils/editor/config';
import { fieldSettingsSectionStyles } from '../../utils/editor/styles';
import TextCharacterLimit from '../components/TextCharacterLimit';

const getTextTypeOptions = (t: TFunction) => [
  {
    Icon: Menu2,
    label: t('Database:ShortText'),
    value: DatabaseTextFormatEnum.SingleLine,
  },
  {
    Icon: Menu3,
    label: t('Database:LongText'),
    value: DatabaseTextFormatEnum.MultiLine,
  },
];

const TextFieldEditorConfig: DatabaseFieldEditorConfig = {
  getFieldSettingsFormProps: ({ t, allowPartialUpdates }) => {
    const textTypeOptions = getTextTypeOptions(t);
    const fields: Field[] = [
      FieldUtils.getDatabaseTypeFormField(t, allowPartialUpdates),
      {
        name: 'viewField.textType',
        label: t('Common:Filter.Type'),
        type: FieldType.Picklist,
        picklistValues: textTypeOptions,
        props: {
          className: FORM_PICKLIST_PROPS.className,
          optionLabelRenderer: renderMediumOptionLabel,
          renderValue: renderMediumSelectValue,
        },
      },
      FieldUtils.getRequiredFormField(t),
      FieldUtils.getDescriptionEnabledFormField(t),
      {
        name: 'metadataField.validation.maxLength',
        type: FieldType.Custom,
        Component: ({ value, onChange }) => {
          return <TextCharacterLimit value={value} onChange={onChange} />;
        },
      },
    ];

    return {
      fields: keyBy(fields, 'name'),
      sectionLayout: [
        {
          id: 'text-type-settings',
          className: fieldSettingsSectionStyles(32),
          layout: ['metadataField.type', 'viewField.textType'],
        },
        {
          id: 'text-field-view-settings',
          className: fieldSettingsSectionStyles(24),
          layout: [
            'viewField.required',
            'viewField.descriptionEnabled',
            'metadataField.validation.maxLength',
          ],
        },
      ],
      overrides: FieldUtils.FormOverrides,
    };
  },
  getFieldSettingsFormInitialValues: ({ metadataField, viewField }) => ({
    metadataField: { type: metadataField.type, validation: metadataField.validation },
    viewField: {
      required: viewField.required,
      descriptionEnabled: viewField.descriptionEnabled,
      textType: viewField.textType,
    },
  }),
  adaptEditorFormField: ({ field, t }) => {
    const isMultiline = field.viewField.textType === DatabaseTextFormatEnum.MultiLine;
    const maxLength = field.metadataField.validation?.maxLength;
    const hasCharLimit = typeof maxLength === 'number' && maxLength > 0;

    return {
      placeholder: t('Database:WriteYourAnswer'),
      size: 'large',
      type: isMultiline ? FieldType.Multiline : FieldType.Text,
      props: {
        rows: 4,
        characterLimit: hasCharLimit ? maxLength : undefined,
      },
    };
  },
};

export default TextFieldEditorConfig;
